/* eslint-disable complexity */
import { breakpoints } from 'style/config';
import ContentConstants from 'constants/content';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import { getContent } from 'services/api/Content/getContent';
import imageUtils from 'utils/Image';
import p13nApi from 'services/api/p13n';
import sdnApi from 'services/api/sdn';
import { homeSelector } from 'selectors/page/home/homeSelector';
import { p13nSelector } from 'selectors/p13n/p13nSelector';
import { SET_HOME_PAGE } from 'constants/actionTypes/home';
import cookieUtils from 'utils/Cookies';
import Empty from 'constants/empty';
import p13nUtils from 'utils/localStorage/P13n';
import { pageSelector } from 'selectors/page/pageSelector';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import CookieUtils from 'utils/Cookies';
import Storage from 'utils/localStorage/Storage';
import Location from 'utils/Location';
import { getOrCreateAbTestUuid } from 'utils/localStorage/AbTestUuid';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import PuwyloHomepageUtils from 'utils/PuwyloHomepageUtils';
import getTrendingContentData, { TRENDING_CONTENT } from 'services/api/bannerList/getTrendingContentData';
import {
    SET_INITIALIZATION,
    SET_P13N_DATA_FOR_PREVIEW,
    SET_P13N_DATA,
    SET_REPLEN_P13N_DATA,
    SET_RECAP_P13N_AB_TEST_DATA,
    SET_PUWYLO_BATCH_DATA,
    SET_TRENDING_CONTENT_DATA,
    SET_TRENDING_CONTENT_LOADING
} from 'constants/actionTypes/personalization';

// WBS-35: bound the PUWYLO batch fetch to 500ms at the thunk layer. The merged
// PDE-2562 client has no AbortController/timeout, so the UI decision is bounded
// here via Promise.race; on deadline the fallback (null) sentinel is dispatched.
const PUWYLO_BATCH_TIMEOUT_MS = 500;

const { COMPONENT_TYPES } = ContentConstants;
const { getImageSrcForPreload } = imageUtils;
const { setPersonalizationCache, setPersonalizationKey, getContextIdsToUpdate, FIVE_MINUTES_IN_MILLISECONDS } = p13nUtils;

const storeTrendingOfferContextIds = contextIds => {
    const existing = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE.TRENDING_OFFERS)) || [];
    const merged = [...new Set([...existing, ...contextIds])];
    window.localStorage.setItem(LOCAL_STORAGE.TRENDING_OFFERS, JSON.stringify(merged));
};

const flushTrendingOffersCache = () => {
    const storedTOContextIds = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE.TRENDING_OFFERS)) || [];
    storedTOContextIds.forEach(id => Storage.local.removeItem(setPersonalizationKey(id)));
    window.localStorage.removeItem(LOCAL_STORAGE.TRENDING_OFFERS);
    Storage.local.removeItem(LOCAL_STORAGE.TRENDING_OFFERS_DEVICE_ID);

    return storedTOContextIds;
};

const flushTrendingOffersOnSignIn = () => (dispatch, getState) => {
    const storedTOContextIds = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE.TRENDING_OFFERS)) || [];

    if (storedTOContextIds.length) {
        const { headData = Empty.Array } = p13nSelector(getState());
        const toContexts = new Set(storedTOContextIds);
        const getContext = row => row?.p13n?.context || row?.context;
        const remainingHeadData = headData.filter(row => !toContexts.has(getContext(row)));

        if (remainingHeadData.length !== headData.length) {
            dispatch(setP13NData(remainingHeadData));
        }
    }
};

// Collect the set of MAB-enabled context ids from items (top-level + children).
// Used to re-route flushed trending-offer contexts that are actually MAB banners
// to the p13n-content /dynamic endpoint instead of the generic p13n endpoint.
// A MAB-enabled banner always has `isEnabled: true` in production, so both must hold.
const collectMabContextIds = (items = Empty.Array) => {
    const mabContexts = new Set();
    const add = personalization => {
        if (personalization?.isEnabled && personalization?.isMABEnabled && personalization?.context) {
            mabContexts.add(personalization.context);
        }
    };
    items.forEach(item => {
        add(item?.personalization);
        (item?.items || Empty.Array).forEach(child => add(child?.personalization));
    });

    return mabContexts;
};

const isNewPage = ({ newLocation, previousLocation }) => {
    return previousLocation.prevPath !== newLocation.newPath;
};

function getImagesToPreload({ items = Empty.Array }) {
    const firstItem = items[0] || Empty.Object;
    let imagesToPreload = Empty.Array;
    const isLargeScreen = window.matchMedia(breakpoints.smMin).matches;
    const viewportWidth = window.innerWidth;
    const imageWidth = isLargeScreen ? firstItem.largeWidth || firstItem.width : firstItem.width;
    const amountOfImagesToShow = Math.ceil(viewportWidth / imageWidth);

    if (firstItem.type === COMPONENT_TYPES.BANNER_LIST) {
        imagesToPreload = firstItem.items
            .map(({ media = Empty.Object, largeMedia = Empty.Object }) => {
                if (!media.src) {
                    return null;
                }

                const mediaSrc = firstItem.largeWidth && isLargeScreen ? largeMedia.src || media.src : media.src;
                const imgWidth = firstItem.largeWidth && isLargeScreen ? firstItem.largeWidth : firstItem.width;

                return getImageSrcForPreload(mediaSrc, imgWidth);
            })
            .slice(0, amountOfImagesToShow);
    }

    return imagesToPreload;
}

function setHomepageData(payload) {
    return {
        type: SET_HOME_PAGE,
        payload
    };
}

const openPage = ({ events: { onDataLoaded, onPageUpdated, onError } }) => {
    return dispatch => {
        const { country, language } = Sephora.renderQueryParams;

        return getContent({
            country,
            language,
            path: '/home'
        })
            .then(response => {
                let data;

                if (!response.data) {
                    // eslint-disable-next-line no-console
                    console.error('No data returned from getContent');
                    data = { items: Empty.Array };
                } else {
                    data = response.data;
                }

                const imagesToPreload = getImagesToPreload(data);
                onDataLoaded(data, imagesToPreload);
                dispatch(setHomepageData(data));
                onPageUpdated(data);
            })
            .catch(onError);
    };
};

const updatePage = () => {};

const setP13NInitialization = payload => {
    return {
        type: SET_INITIALIZATION,
        payload
    };
};

const setP13NDataForPreview = payload => {
    return {
        type: SET_P13N_DATA_FOR_PREVIEW,
        payload
    };
};

const setP13NData = payload => {
    return {
        type: SET_P13N_DATA,
        payload
    };
};

const setReplenP13NData = payload => {
    return {
        type: SET_REPLEN_P13N_DATA,
        payload
    };
};

const setRecapP13nAbTestData = payload => {
    return {
        type: SET_RECAP_P13N_AB_TEST_DATA,
        payload
    };
};

const setPuwyloBatchData = payload => {
    return {
        type: SET_PUWYLO_BATCH_DATA,
        payload
    };
};

const setTrendingContentData = ({ placement, variationData, akamaiabtestdata }) => {
    return {
        type: SET_TRENDING_CONTENT_DATA,
        payload: { placement, variationData, akamaiabtestdata }
    };
};

const setTrendingContentLoading = placement => {
    return {
        type: SET_TRENDING_CONTENT_LOADING,
        payload: { placement }
    };
};

// Thunk dispatched by BannerList on mount (replaces the old component-local fetch + setState).
// Marks the placement pending synchronously so the banner keeps a sticky skeleton for the whole
// request, then fetches and stores the result in Redux keyed by placement. getTrendingContentData
// never rejects (internal error fallback), so this always resolves and dispatches.
const loadTrendingContent =
    ({ blPlacement, personalization }) =>
        async dispatch => {
            const placement = TRENDING_CONTENT[blPlacement];

            dispatch(setTrendingContentLoading(placement));

            const { variationData, akamaiabtestdata } = await getTrendingContentData({ blPlacement, personalization });

            dispatch(setTrendingContentData({ placement, variationData, akamaiabtestdata }));
        };

const fetchPuwyloBatchData =
    ({
        contextIds, userId, atgId, constructorClientId, constructorSessionId
    } = {}) =>
        async (dispatch, getState) => {
            if (!PuwyloHomepageUtils.isPuwyloHomepageExpansionEnabled()) {
                return;
            }

            // PROVISIONAL (backend in-flight): skip the actual batch API call until the
            // PUWLO batch endpoint is ready. Remove once the backend is live.
            if (!PuwyloHomepageUtils.isPuwyloBatchApiEnabled()) {
                dispatch(setPuwyloBatchData(null));

                return;
            }

            // CR-002: derive contextIds from the personalized component list when not
            // supplied by the caller (Recap.ctrlr passes this.props which omits contextIds).
            let resolvedContextIds = contextIds;

            if (!resolvedContextIds && typeof getState === 'function') {
                const state = getState();
                const { items = [] } = homeSelector(state);
                const { p13nContextIds = [] } = getContextIdsToUpdate(items, false);
                resolvedContextIds = p13nContextIds;
            }

            // No contexts to personalize → skip the call and fall back to the existing
            // carousel (a batch request with empty contextIds has nothing to return).
            if (!Array.isArray(resolvedContextIds) || resolvedContextIds.length === 0) {
                dispatch(setPuwyloBatchData(null));

                return;
            }

            const { channel } = Sephora.renderQueryParams || {};
            const locale = LanguageLocaleUtils.getCurrentLanguageLocale();

            try {
                const result = await Promise.race([
                    p13nApi.getP13nPuwylosBatchData({
                        contextIds: resolvedContextIds,
                        userId,
                        atgId,
                        channel,
                        locale,
                        constructorClientId,
                        constructorSessionId
                    }),
                    new Promise(resolve => setTimeout(() => resolve(null), PUWYLO_BATCH_TIMEOUT_MS))
                ]);

                // The batch API client (getP13nPuwylosBatchData) normalizes some HTTP
                // errors into { cards: [], ... } rather than null, so hasCards is the
                // reliable indicator of a usable response. null sentinel = failure/timeout;
                // non-null result with cards = success. shouldUsePuwyloCarousel handles
                // empty cards correctly for the render decision.
                const hasCards = result && Array.isArray(result.cards) && result.cards.length > 0;

                if (!hasCards) {
                    Sephora.logger.warn('PUWYLO BATCH: empty/failed batch response — falling back to existing carousel');
                }

                dispatch(setPuwyloBatchData(hasCards ? result : null));
            } catch (error) {
                Sephora.logger.error('PUWYLO BATCH: thunk failed to fetch batch data', error);
                dispatch(setPuwyloBatchData(null));
            }
        };

const setPersonalizationAnalyticsData = p13nData => () => {
    const analyticsData = [];

    Array.isArray(p13nData) &&
        p13nData?.forEach(item => {
            const {
                context, variation, ruleId, ruleSetId, isControl, isAbTest, isDefault
            } = item.p13n || Empty.Object;

            const getBooleanValue = value => {
                if (value === true) {
                    return value;
                } else if (value === false) {
                    return false;
                }

                return 'n/a';
            };

            const c = 'c=' + (context || 'n/a');
            const v = 'v=' + (variation || 'n/a');
            const rid = 'rid=' + (ruleId || 'n/a');
            const rsid = 'rsid=' + (ruleSetId || 'n/a');
            const isc = 'isc=' + getBooleanValue(isControl);
            const isab = 'isab=' + getBooleanValue(isAbTest);
            const isd = 'isd=' + getBooleanValue(isDefault);

            const data = [c, v, rid, rsid, isc, isab, isd];
            analyticsData.push(data.join(';'));
        });
    digitalData.page.attributes.p13nAnalyticsData = analyticsData.join(',');
};

const getPersonalizedEnabledComponents =
    (itemsList = []) =>
        async (dispatch, getState) => {
            dispatch(setP13NInitialization(false));
            const reduxState = getState();
            const prvCookie = cookieUtils.read(cookieUtils.KEYS.P13N_PRV);

            const {
                isAnonymous, isInitialized, userId: atgId, biId, defaultSAZipCode
            } = coreUserDataSelector(reduxState);
            const p13n = p13nSelector(reduxState);
            const page = pageSelector(reduxState);
            const persistentBannerItems = page.headerFooterTemplate?.data?.persistentBanner || Empty.Array;
            const megaNavData = page.headerFooterTemplate?.data?.megaNav || Empty.Object;
            const beautyOffersPageItems = page?.content?.layout?.content || Empty.Array;
            const rwdBasketTopContentItems = page?.rwdBasket?.cmsData?.topContent || Empty.Array;
            const { items = Empty.Array } = homeSelector(reduxState);
            const totalItems = items.concat(persistentBannerItems, beautyOffersPageItems, rwdBasketTopContentItems, itemsList);
            const customerData = Storage.session.getItem(LOCAL_STORAGE.CUSTOMER_OBJECT);

            if (Object.keys(megaNavData).length) {
                totalItems.push(megaNavData);
            }

            if (Sephora.configurationSettings?.isBirthdayLandingPageP13NEnabled) {
                if (Location.isBirthdayGiftPage()) {
                    const topBanner = page?.content?.layout?.heroBanner;
                    topBanner.personalization = {
                        ...topBanner?.personalization,
                        isBirthdayGift: true
                    };
                    totalItems.push(topBanner);
                }
            }

            // Skip P13N API calls for SEO bots
            if (Sephora.isSEO) {
                return dispatch(setP13NInitialization(true));
            }

            const isPrvEnv = prvCookie && !p13n.isInitialized;
            // Allow P13N flows for anonymous users as well (PUWYLO logged-out enablement).
            // We still guard on isInitialized and p13n.isInitialized to avoid infinite loaders / duplicate calls.
            const otherEnvs = !p13n.isInitialized && isInitialized;

            const localStorageProfileId = Storage.local.getItem(LOCAL_STORAGE.PROFILE_ID);
            // recap
            const recapComponent = totalItems.find(item => item?.type === COMPONENT_TYPES.RECAP);
            const recapPersonalization = recapComponent?.personalization;
            const recapContextId = recapPersonalization?.context;
            const recapAnonymousAB = recapPersonalization?.anonymousAB;
            const shouldFetchRecapAbTestData =
            isAnonymous && recapPersonalization?.isAnonymousEnabled === true && recapAnonymousAB?.abTest === true && Boolean(recapContextId);

            // trending offers NBO — anonymous-only, mirrors p13n.headScript.js TO flow
            const trendingOffersItem =
            isAnonymous &&
            Sephora.configurationSettings?.p13ntrendingNBO?.isEnabled === true &&
            totalItems.find(
                item =>
                    (item?.type === COMPONENT_TYPES.PROMOTION_LIST || item?.type === COMPONENT_TYPES.BANNER) &&
                    item?.personalization?.isTrendingOffersEnabled
            );
            const trendingOffersPersonalization = trendingOffersItem?.personalization;
            const shouldFetchTrendingOffers = Boolean(trendingOffersItem);

            // Needed so that if the user is not logged in and there is no PRV cookie, we can set P13N as initialized to avoid infinite loaders
            // in components that depends on this value
            if (!localStorageProfileId && !prvCookie && !shouldFetchRecapAbTestData && !shouldFetchTrendingOffers) {
                return dispatch(setP13NInitialization(true));
            }

            const { country, channel, language } = Sephora.renderQueryParams;

            // Recap AB-test fetch runs independently of isPrvEnv / otherEnvs because the
            // experiment is anonymous-only and may not satisfy those gates. The head
            // script also kicks off this fetch in parallel; the result is idempotent
            // (same Session-ID UUID → same bucket), so the last write wins safely.
            if (shouldFetchRecapAbTestData) {
                try {
                    const recapAbTestResults = await sdnApi.getP13nRecapABTestData({
                        channel,
                        country,
                        language,
                        contextId: recapContextId,
                        abTestEndDate: recapAnonymousAB?.abTestEndDate
                    });

                    dispatch(setRecapP13nAbTestData(recapAbTestResults));
                } catch (error) {
                    Sephora.logger.error('Error getting Recap AB test data:', error);
                    dispatch(setRecapP13nAbTestData(null));
                }
            } else if (isAnonymous) {
            // Clear AB test data when the recap enablement toggle is off
            // to avoid stale state across navigations.
                dispatch(setRecapP13nAbTestData(null));
            }

            if (isPrvEnv || otherEnvs) {
                const {
                    p13nContextIds: filteredP13nContextIds = Empty.Array,
                    nbcContextIds = Empty.Array,
                    bdContextIds = Empty.Array,
                    mabContextIds = Empty.Array,
                    replenContextIds = Empty.Array,
                    trendingOfferContextIds = Empty.Array
                } = getContextIdsToUpdate(totalItems, shouldFetchTrendingOffers);

                let p13nContextIds = [...filteredP13nContextIds];
                let mabContextEntryIds = [...mabContextIds];

                if (!isAnonymous) {
                    const storedTOContextIds = flushTrendingOffersCache();

                    if (storedTOContextIds.length) {
                    // Flushed trending-offer contexts may actually be MAB banners. Route them
                    // by their real CMS rule type: MAB → /dynamic (p13n-content) so inline
                    // entities (promotion, first_name) resolve; everything else → generic p13n.
                        const mabContexts = collectMabContextIds(totalItems);
                        storedTOContextIds.forEach(contextId => {
                            if (mabContexts.has(contextId)) {
                                mabContextEntryIds.push(contextId);
                            } else {
                                const personalizationEnabledElement = totalItems.find(item => item?.personalization?.context === contextId)
                                    ?.personalization?.isEnabled;

                                if (personalizationEnabledElement) {
                                    p13nContextIds.push(contextId);
                                }
                            }
                        });
                        mabContextEntryIds = [...new Set(mabContextEntryIds)];
                        p13nContextIds = [...new Set(p13nContextIds)];
                    }
                }

                const contextEntryIds = [
                    ...p13nContextIds,
                    ...nbcContextIds,
                    ...bdContextIds,
                    ...mabContextEntryIds,
                    ...replenContextIds,
                    ...trendingOfferContextIds
                ];

                if (!contextEntryIds.length) {
                    return dispatch(setP13NInitialization(true));
                }

                // Logged-out users may not have a profileId/atgId yet.
                // If it is missing, skip non-trending P13N calls.
                const atgIdForP13n = atgId;

                if (!prvCookie && !atgIdForP13n && !shouldFetchTrendingOffers) {
                    return Promise.resolve();
                }

                let personalizationData = [];

                if (customerData && !customerData.atg_id) {
                // eslint-disable-next-line camelcase
                    customerData.atg_id = atgIdForP13n;
                    Storage.session.setItem(LOCAL_STORAGE.CUSTOMER_OBJECT, customerData);
                }

                if (nbcContextIds?.length && atgIdForP13n) {
                    try {
                        const nbcData = await p13nApi.getP13nNbcData({
                            channel,
                            country,
                            language,
                            atgId: atgIdForP13n,
                            contextEntryIds: nbcContextIds,
                            zipCode: defaultSAZipCode
                        });

                        if (nbcData?.length) {
                            personalizationData = personalizationData.concat(nbcData);
                        }
                    } catch (error) {
                        Sephora.logger.error('Error getting NBC data:', error);
                    }
                }

                if (mabContextEntryIds?.length && atgIdForP13n) {
                    try {
                        const mabData = await p13nApi.getP13nMABData({
                            channel,
                            country,
                            language,
                            atgId: atgIdForP13n,
                            contextEntryIds: mabContextEntryIds,
                            zipCode: defaultSAZipCode
                        });

                        if (mabData?.length) {
                            personalizationData = personalizationData.concat(mabData);
                        }
                    } catch (error) {
                        Sephora.logger.error('Error getting MAB data:', error);
                    }
                }

                if (bdContextIds?.length && atgIdForP13n && Sephora.configurationSettings?.isBirthdayLandingPageP13NEnabled) {
                    try {
                        const birthdayData = await p13nApi.getP13nBirthdayData({
                            channel,
                            country,
                            language,
                            atgId: atgIdForP13n,
                            contextEntryIds: bdContextIds,
                            zipCode: defaultSAZipCode
                        });

                        if (birthdayData?.length) {
                            personalizationData = personalizationData.concat(birthdayData);
                        }
                    } catch (error) {
                        Sephora.logger.error('Error getting Birthday data:', error);
                    }
                }

                if (replenContextIds?.length && atgIdForP13n) {
                    try {
                    // visit any plp or pdp page and back to homescreen, this get hit
                        const replenResults = await p13nApi.getP13nReplenData({
                            channel,
                            country,
                            language,
                            atgId: atgIdForP13n,
                            contextId: replenContextIds[0]
                        });

                        if (replenResults) {
                            dispatch(setReplenP13NData(replenResults));
                        }
                    } catch (error) {
                        Sephora.logger.error('Error getting Replenishment P13N data:', error);
                    }
                }

                if (cookieUtils.read(cookieUtils.KEYS.P13N_PRV) && customerData) {
                    if (!CookieUtils.read(CookieUtils.KEYS.PREVIEW_CUSTOMER)) {
                        const cookieData = {
                            ...customerData,
                            context: contextEntryIds,
                            zipCode: defaultSAZipCode
                        };
                        CookieUtils.write(CookieUtils.KEYS.PREVIEW_CUSTOMER, btoa(JSON.stringify(cookieData)), null, true, false);
                    }

                    const previewData = await p13nApi.getPreviewP13nData();

                    if (previewData?.length) {
                        personalizationData = personalizationData.concat(previewData);
                    }
                } else {
                    if (trendingOfferContextIds?.length) {
                        const trendingOffersAbTest = trendingOffersPersonalization?.trendingOffersABTest?.[0];
                        const trendingOffersContextId = trendingOffersAbTest?.abTestId || trendingOffersPersonalization?.context;
                        const trendingOffersDeviceId = getOrCreateAbTestUuid(
                            trendingOffersContextId,
                            trendingOffersAbTest?.abTestEndDate || trendingOffersPersonalization?.endDate
                        );

                        if (trendingOffersDeviceId) {
                            Storage.local.setItem(LOCAL_STORAGE.TRENDING_OFFERS_DEVICE_ID, trendingOffersDeviceId);

                            try {
                                const trendingData = await p13nApi.getP13nData({
                                    channel,
                                    country,
                                    language,
                                    atgId: 0,
                                    biId,
                                    contextEntryIds: trendingOfferContextIds,
                                    zipCode: defaultSAZipCode,
                                    customAttribute: `deviceId=${trendingOffersDeviceId}`
                                });

                                if (trendingData?.length) {
                                    setPersonalizationCache(trendingData, FIVE_MINUTES_IN_MILLISECONDS);
                                    storeTrendingOfferContextIds(trendingOfferContextIds);
                                    personalizationData = personalizationData.concat(trendingData);
                                }
                            } catch (error) {
                            //eslint-disable-next-line no-console
                                console.error('Error getting trending offers P13N data:', error);
                            }
                        }
                    }

                    if (p13nContextIds?.length && atgIdForP13n) {
                        try {
                            const p13nData = await p13nApi.getP13nData({
                                channel,
                                country,
                                language,
                                atgId: atgIdForP13n,
                                biId,
                                contextEntryIds: p13nContextIds,
                                zipCode: defaultSAZipCode
                            });

                            if (p13nData?.length) {
                                personalizationData = personalizationData.concat(p13nData);
                            }
                        } catch (error) {
                        //eslint-disable-next-line no-console
                            console.error('Error getting P13N data:', error);
                            dispatch(setP13NInitialization(true));
                        }
                    }
                }

                if (!personalizationData.length) {
                    dispatch(setP13NInitialization(true));

                    return Promise.resolve();
                }

                if (prvCookie) {
                    dispatch(setP13NDataForPreview(personalizationData));
                } else {
                    setPersonalizationCache(personalizationData);
                    // Refresh Redux headData so selectors that read p13n.headData (e.g. "For You")
                    // reflect freshly fetched data after an in-page sign-in. getContextIdsToUpdate
                    // skips already-cached contexts, so personalizationData is partial — merge it
                    // into the existing headData by context instead of replacing.
                    const existingHeadData = p13n.headData || Empty.Array;
                    const getContext = row => row?.p13n?.context || row?.context;
                    const freshContexts = new Set(personalizationData.map(getContext));
                    const mergedHeadData = [...existingHeadData.filter(row => !freshContexts.has(getContext(row))), ...personalizationData];
                    dispatch(setP13NData(mergedHeadData));
                    dispatch(setP13NInitialization(true));
                    dispatch(setPersonalizationAnalyticsData(mergedHeadData));
                }

                return Promise.resolve();
            } else {
            // Set as true to prevent infinite loaders in components
            // which depends purely on this value to show it
                dispatch(setP13NInitialization(true));

                return Promise.resolve();
            }
        };

export default {
    getPersonalizedEnabledComponents,
    flushTrendingOffersCache,
    flushTrendingOffersOnSignIn,
    isNewPage,
    openPage,
    setP13NInitialization,
    updatePage,
    setP13NDataForPreview,
    setP13NData,
    setPersonalizationAnalyticsData,
    setReplenP13NData,
    setRecapP13nAbTestData,
    setPuwyloBatchData,
    fetchPuwyloBatchData,
    setTrendingContentData,
    setTrendingContentLoading,
    loadTrendingContent
};
